// Avatar del jugador de Modo Carrera.
//
// La CABEZA (cara + pelo + barba + lentes + cuello natural) la genera DiceBear (avataaars). El
// resto del cuerpo (torso + prenda + brazos + manos con dedos + piernas + botines + accesorios) lo
// dibujamos nosotros, con variedad real de personalización (no solo colores): tipo de prenda,
// patrón, accesorios, estilos de botín, etc. Todo es SVG (sirve en pantalla y en la imagen).
import React, { useMemo } from 'react';
import { createAvatar } from '@dicebear/core';
import { avataaars } from '@dicebear/collection';

export const AVATAR_DEFAULT = {
    piel: '#e0ac69',
    peinado: 'shortFlat',
    pelo_color: '#2d1b0e',
    barba: 'ninguna',
    lentes: 'ninguno',
    lentes_color: '#1f2937',
    atuendo: 'remera',
    patron: 'liso',
    camiseta: '#facc15',
    color2: '#ffffff',
    short_color: '#1f2937',
    medias: '#facc15',
    botines: '#111111',
    botin_estilo: 'clasico',
    accesorio: 'ninguno',
    accesorio_color: '#facc15',
};

// Paletas de color.
export const PIELES = ['#ffdbb4', '#f5d3a8', '#e0ac69', '#c68642', '#8d5524', '#5c3317', '#3b2417', '#27160b'];
export const PELO_COLORES = ['#2d1b0e', '#0d0d0d', '#7b4a12', '#c98a2c', '#d9d9d9', '#b22222', '#5b3a1a'];
export const COLORES = ['#facc15', '#dc2626', '#2563eb', '#16a34a', '#ffffff', '#111827', '#9333ea', '#f97316', '#06b6d4', '#0ea5e9', '#84cc16', '#ec4899', '#7f1d1d', '#1e3a8a'];
export const CAMISETAS = COLORES;
export const COLOR2 = COLORES;
export const SHORTS = ['#1f2937', '#ffffff', '#111111', '#facc15', '#dc2626', '#2563eb', '#16a34a', '#9333ea', '#f97316', '#0ea5e9', '#7f1d1d', '#374151'];
export const MEDIAS = ['#facc15', '#ffffff', '#111111', '#dc2626', '#2563eb', '#16a34a', '#9333ea', '#f97316', '#06b6d4', '#ec4899', '#1e3a8a', '#374151'];
export const BOTINES = ['#111111', '#ffffff', '#dc2626', '#2563eb', '#facc15', '#f97316', '#16a34a', '#9333ea', '#ec4899', '#06b6d4'];

export const PEINADOS = [
    { value: 'shortFlat', label: 'Corto' }, { value: 'shortWaved', label: 'Ondulado' },
    { value: 'shortCurly', label: 'Rulos corto' }, { value: 'shortRound', label: 'Casquete' },
    { value: 'theCaesar', label: 'César' }, { value: 'theCaesarAndSidePart', label: 'César con raya' },
    { value: 'sides', label: 'Costados' }, { value: 'shavedSides', label: 'Rapado' },
    { value: 'straight01', label: 'Lacio' }, { value: 'straight02', label: 'Lacio 2' },
    { value: 'straightAndStrand', label: 'Lacio con mechón' }, { value: 'longButNotTooLong', label: 'Media melena' },
    { value: 'frizzle', label: 'Crespo' }, { value: 'curly', label: 'Rulos' },
    { value: 'curvy', label: 'Ondulado largo' }, { value: 'shaggy', label: 'Despeinado' },
    { value: 'bob', label: 'Carré' }, { value: 'fro', label: 'Afro' },
    { value: 'froBand', label: 'Afro + vincha' }, { value: 'dreads01', label: 'Rastas' },
    { value: 'dreads02', label: 'Rastas 2' }, { value: 'dreads', label: 'Rastas largas' },
    { value: 'bun', label: 'Rodete' }, { value: 'bigHair', label: 'Melena' },
    { value: 'shaggyMullet', label: 'Mullet' }, { value: 'hat', label: 'Gorra' },
    { value: 'turban', label: 'Turbante' }, { value: 'winterHat1', label: 'Gorro' },
    { value: 'pelado', label: 'Pelado' },
];

export const BARBAS = [
    { value: 'ninguna', label: 'Sin barba' }, { value: 'beardLight', label: 'Candado' },
    { value: 'beardMedium', label: 'Media' }, { value: 'beardMajestic', label: 'Barbón' },
    { value: 'moustacheFancy', label: 'Bigote' }, { value: 'moustacheMagnum', label: 'Bigotón' },
];

export const LENTES = [
    { value: 'ninguno', label: 'Ninguno' }, { value: 'round', label: 'Redondos' },
    { value: 'sunglasses', label: 'De sol' }, { value: 'wayfarers', label: 'Wayfarer' },
    { value: 'prescription01', label: 'Recetados' }, { value: 'prescription02', label: 'Recetados 2' },
    { value: 'kurt', label: 'Kurt' }, { value: 'eyepatch', label: 'Parche' },
];
export const LENTES_COLORES = ['#1f2937', '#111827', '#2563eb', '#dc2626', '#16a34a', '#9333ea', '#f97316', '#7f1d1d', '#d97706', '#ffffff'];

// Prenda de arriba (dibujada).
export const ATUENDOS = [
    { value: 'remera', label: 'Remera' },
    { value: 'remera_v', label: 'Remera cuello V' },
    { value: 'manga_larga', label: 'Manga larga' },
    { value: 'buzo', label: 'Buzo / canguro' },
    { value: 'sueter', label: 'Suéter' },
    { value: 'campera', label: 'Campera' },
    { value: 'traje', label: 'Traje' },
    { value: 'conjunto', label: 'Conjunto deportivo' },
];

// Patrón de la camiseta (solo en prendas de tela lisa: remera/manga larga/conjunto).
export const PATRONES = [
    { value: 'liso', label: 'Liso' },
    { value: 'rayas_v', label: 'Rayas verticales' },
    { value: 'rayas_h', label: 'Rayas horizontales' },
    { value: 'banda', label: 'Banda' },
];

export const BOTIN_ESTILOS = [
    { value: 'clasico', label: 'Clásicos' },
    { value: 'altos', label: 'Media caña' },
    { value: 'franja', label: 'Con franja' },
];

export const ACCESORIOS = [
    { value: 'ninguno', label: 'Ninguno' },
    { value: 'capitan', label: 'Cinta de capitán' },
    { value: 'vincha', label: 'Vincha' },
    { value: 'guantes', label: 'Guantes' },
    { value: 'munequeras', label: 'Muñequeras' },
];

const PEINADO_LEGACY = {
    corto: 'shortFlat', rapado: 'shortRound', rizado: 'curly', largo: 'straight01',
    mohicano: 'shavedSides', afro: 'fro', colita: 'bun', trenzas: 'dreads01', pelado: 'pelado',
};
// Sistema anterior usaba `cuello` (redondo/v/manga_larga); ahora es `atuendo`.
const ATUENDO_LEGACY = {
    redondo: 'remera', v: 'remera_v', manga_larga: 'manga_larga',
    shirtCrewNeck: 'remera', shirtVNeck: 'remera_v', shirtScoopNeck: 'remera',
    hoodie: 'buzo', collarAndSweater: 'sueter',
};

const hex = (c) => (c || '').replace('#', '') || '000000';
// Oscurece un color hex (#rrggbb) por un factor, para detalles/sombras de la prenda.
const darken = (h, f = 0.7) => {
    const m = /^#([0-9a-fA-F]{6})$/.exec(h || '');
    if (!m) return h;
    const n = parseInt(m[1], 16);
    const r = Math.round(((n >> 16) & 255) * f), g = Math.round(((n >> 8) & 255) * f), b = Math.round((n & 255) * f);
    return '#' + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
};
const TORSO_LISO = 'M78 168 Q106 158 120 158 Q140 170 160 158 Q174 158 222 168 L214 316 Q150 330 86 316 Z';
const PATRON_OK = ['remera', 'remera_v', 'manga_larga'];
const SLEEVE_LONG = ['manga_larga', 'buzo', 'sueter', 'campera', 'traje', 'conjunto'];

export function normAvatar(input) {
    const a = { ...AVATAR_DEFAULT, ...(input || {}) };
    if (input && input.pelo_estilo && !input.peinado) a.peinado = PEINADO_LEGACY[input.pelo_estilo] || 'shortFlat';
    if (input && typeof input.barba === 'boolean') a.barba = input.barba ? 'beardMedium' : 'ninguna';
    if (input && input.cuello && !input.atuendo) a.atuendo = ATUENDO_LEGACY[input.cuello] || 'remera';
    if (!ATUENDOS.some((x) => x.value === a.atuendo)) a.atuendo = 'remera';
    if (!PATRONES.some((x) => x.value === a.patron)) a.patron = 'liso';
    if (!a.color2) a.color2 = '#ffffff';
    if (!input || !input.medias) a.medias = a.camiseta;
    if (!a.botin_estilo) a.botin_estilo = 'clasico';
    if (!ACCESORIOS.some((x) => x.value === a.accesorio)) a.accesorio = 'ninguno';
    if (!a.accesorio_color) a.accesorio_color = '#facc15';
    if (!a.lentes_color) a.lentes_color = '#1f2937';
    return a;
}

export function avatarScale(altura, peso) {
    if (!Number.isFinite(altura) || !Number.isFinite(peso)) return null;
    const sy = Math.max(0.85, Math.min(1.15, 1 + (altura - 180) * 0.004));
    const dev = peso - (altura - 100);
    const sx = Math.max(0.8, Math.min(1.28, 1 + (dev / 25) * 0.2));
    return { sx, sy };
}

function headInner(a, uid) {
    const pelado = a.peinado === 'pelado';
    const conBarba = a.barba && a.barba !== 'ninguna';
    const conLentes = a.lentes && a.lentes !== 'ninguno';
    const svg = createAvatar(avataaars, {
        size: 280, backgroundColor: ['transparent'],
        eyes: ['default'], eyebrows: ['defaultNatural'], mouth: ['smile'],
        skinColor: [hex(a.piel)],
        top: [pelado ? 'shortFlat' : a.peinado], topProbability: pelado ? 0 : 100,
        hairColor: [hex(a.pelo_color)],
        facialHair: conBarba ? [a.barba] : ['beardLight'], facialHairProbability: conBarba ? 100 : 0,
        facialHairColor: [hex(a.pelo_color)],
        clothing: ['shirtCrewNeck'], clothesColor: [hex(a.camiseta)],
        accessories: conLentes ? [a.lentes] : ['round'], accessoriesProbability: conLentes ? 100 : 0,
        accessoriesColor: [hex(a.lentes_color)],
    }).toString();
    const inner = svg.replace(/^<svg[^>]*>/, '').replace(/<\/svg>\s*$/, '');
    const S = 0.84, HX = 150 - 140 * S, HY = 149 - 185 * S;
    return `<defs><clipPath id="csHead${uid}"><rect x="44" y="0" width="192" height="200"/></clipPath></defs>`
        + `<g transform="translate(${HX} ${HY}) scale(${S})"><g clip-path="url(#csHead${uid})">${inner}</g></g>`;
}

// Patrón de la camiseta, recortado a la silueta del torso.
function patternInner(a, uid) {
    if (!PATRON_OK.includes(a.atuendo) || !a.patron || a.patron === 'liso') return '';
    const c2 = a.color2;
    let inner = '';
    if (a.patron === 'rayas_v') for (let x = 84; x < 216; x += 28) inner += `<rect x="${x}" y="150" width="14" height="180" fill="${c2}"/>`;
    if (a.patron === 'rayas_h') for (let y = 172; y < 316; y += 26) inner += `<rect x="70" y="${y}" width="160" height="13" fill="${c2}"/>`;
    if (a.patron === 'banda') inner += `<path d="M70 196 L230 250 L230 276 L70 222 Z" fill="${c2}"/>`;
    return `<clipPath id="csTorso${uid}"><path d="${TORSO_LISO}"/></clipPath><g clip-path="url(#csTorso${uid})">${inner}</g>`;
}

function handsInner(a) {
    const gl = a.accesorio === 'guantes';
    const col = gl ? a.accesorio_color : a.piel;
    const rx = gl ? 16 : 14, ry = gl ? 14 : 12;
    const one = (cx) => `<g fill="${col}"><ellipse cx="${cx}" cy="291" rx="${rx}" ry="${ry}"/>`
        + `<rect x="${cx - 12}" y="293" width="5" height="13" rx="2.5"/><rect x="${cx - 5}" y="295" width="5" height="14" rx="2.5"/>`
        + `<rect x="${cx + 1}" y="295" width="5" height="14" rx="2.5"/><rect x="${cx + 7}" y="293" width="5" height="12" rx="2.5"/></g>`;
    let m = one(72) + one(228);
    if (a.accesorio === 'munequeras') m += `<rect x="58" y="276" width="28" height="10" rx="3" fill="${a.accesorio_color}"/><rect x="214" y="276" width="28" height="10" rx="3" fill="${a.accesorio_color}"/>`;
    return m;
}

// Torso + brazos + prenda + accesorios + manos. Cada prenda tiene detalles MARCADOS para que se
// distingan claramente (capucha, cuello, cierre, ribetes, franjas).
function upperBody(a, uid) {
    const { piel, camiseta: cam, color2: c2, atuendo } = a;
    const longS = SLEEVE_LONG.includes(atuendo);
    const armColor = longS ? cam : piel;
    const neck = atuendo === 'remera_v' ? 'L140 184 L160 158' : 'Q140 170 160 158';
    const torso = TORSO_LISO.replace('Q140 170 160 158', neck);
    const p = [
        `<rect x="60" y="170" width="24" height="120" rx="12" fill="${armColor}"/>`,
        `<rect x="216" y="170" width="24" height="120" rx="12" fill="${armColor}"/>`,
    ];
    if (longS) {
        p.push(`<rect x="60" y="268" width="24" height="20" rx="10" fill="${piel}"/>`);
        p.push(`<rect x="216" y="268" width="24" height="20" rx="10" fill="${piel}"/>`);
    }
    if (atuendo === 'remera_v') p.push(`<path d="M134 158 L150 184 L166 158 Z" fill="${piel}"/>`);
    p.push(`<path d="M${torso.slice(1)}" fill="${cam}"/>`);
    p.push(patternInner(a, uid));
    // mangas (cap del hombro)
    if (longS) {
        p.push(`<path d="M82 166 Q58 168 52 196 L72 204 L96 174 Z" fill="${cam}"/>`);
        p.push(`<path d="M218 166 Q242 168 248 196 L228 204 L204 174 Z" fill="${cam}"/>`);
    } else {
        p.push(`<path d="M82 166 Q58 168 50 202 L84 214 L96 176 Z" fill="${cam}"/>`);
        p.push(`<path d="M218 166 Q242 168 250 202 L216 214 L204 176 Z" fill="${cam}"/>`);
    }
    // detalles MARCADOS por prenda
    if (atuendo === 'buzo') {
        p.push(`<path d="M96 172 Q150 132 204 172 Q150 158 96 172 Z" fill="${darken(cam, 0.8)}"/>`);
        p.push(`<path d="M120 160 Q150 150 180 160 Q150 184 120 160 Z" fill="${c2}"/>`);
        p.push(`<path d="M104 250 H196 V286 Q150 298 104 286 Z" fill="${darken(cam, 0.82)}"/>`);
        p.push(`<line x1="104" y1="252" x2="196" y2="252" stroke="${c2}" stroke-width="3"/>`);
        p.push(`<rect x="140" y="170" width="4" height="40" rx="2" fill="${c2}"/><rect x="156" y="170" width="4" height="40" rx="2" fill="${c2}"/><circle cx="142" cy="212" r="4" fill="${c2}"/><circle cx="158" cy="212" r="4" fill="${c2}"/>`);
    } else if (atuendo === 'sueter') {
        p.push(`<path d="M122 160 Q150 150 178 160 L178 176 Q150 166 122 176 Z" fill="${c2}"/>`);
        p.push(`<path d="M124 150 Q150 142 176 150 L176 160 Q150 152 124 160 Z" fill="${c2}"/>`);
        p.push(`<rect x="58" y="278" width="26" height="12" rx="4" fill="${c2}"/><rect x="216" y="278" width="26" height="12" rx="4" fill="${c2}"/>`);
        p.push(`<rect x="86" y="302" width="128" height="14" rx="4" fill="${c2}"/>`);
    } else if (atuendo === 'campera') {
        p.push(`<path d="M120 158 L150 174 L120 178 Z" fill="${c2}"/><path d="M180 158 L150 174 L180 178 Z" fill="${c2}"/>`);
        p.push(`<rect x="145" y="166" width="10" height="150" fill="${c2}"/>`);
        p.push(`<rect x="143" y="176" width="14" height="10" rx="2" fill="${darken(c2, 0.7)}"/>`);
        p.push(`<rect x="58" y="278" width="26" height="12" rx="4" fill="${c2}"/><rect x="216" y="278" width="26" height="12" rx="4" fill="${c2}"/>`);
    } else if (atuendo === 'traje') {
        p.push(`<path d="M150 162 L118 170 L150 256 Z" fill="${darken(cam, 0.75)}"/><path d="M150 162 L182 170 L150 256 Z" fill="${darken(cam, 0.6)}"/>`);
        p.push(`<path d="M138 162 H162 L150 210 Z" fill="#eef2f7"/>`);
        p.push(`<path d="M146 166 H154 L158 210 L150 222 L142 210 Z" fill="${c2}"/>`);
    } else if (atuendo === 'conjunto') {
        p.push(`<path d="M126 158 Q150 152 174 158 L174 168 Q150 162 126 168 Z" fill="${c2}"/>`);
        p.push(`<rect x="146" y="162" width="8" height="60" fill="${c2}"/>`);
        p.push(`<rect x="60" y="172" width="5" height="116" fill="${c2}"/><rect x="235" y="172" width="5" height="116" fill="${c2}"/>`);
        p.push(`<rect x="84" y="176" width="5" height="40" fill="${c2}"/><rect x="211" y="176" width="5" height="40" fill="${c2}"/>`);
    }
    // accesorios del torso/brazos
    if (a.accesorio === 'capitan') p.push(`<rect x="58" y="196" width="28" height="14" rx="3" fill="${a.accesorio_color}"/><rect x="58" y="200" width="28" height="3" fill="rgba(0,0,0,0.5)"/>`);
    p.push(handsInner(a));
    return p.join('');
}

function bootsInner(bo, estilo) {
    const L = (y, h, extra = '') => `<path d="M90 ${y} h48 a8 8 0 0 1 8 8 v${h} h-72 v-${h} a8 8 0 0 1 16 -8 z" fill="${bo}"/>${extra}`;
    const R = (y, h, extra = '') => `<path d="M170 ${y} h48 a8 8 0 0 1 8 8 v${h} h-72 v-${h} a8 8 0 0 1 16 -8 z" fill="${bo}"/>${extra}`;
    if (estilo === 'altos') return L(426, 16) + R(426, 16);
    if (estilo === 'franja') {
        return L(434, 8, `<rect x="74" y="439" width="72" height="3" rx="1.5" fill="#ffffffcc"/>`)
            + R(434, 8, `<rect x="154" y="439" width="72" height="3" rx="1.5" fill="#ffffffcc"/>`);
    }
    return L(434, 8) + R(434, 8);
}

function legsInner(a) {
    const { piel, short_color: sh, medias: me, botines: bo } = a;
    return [
        `<rect x="100" y="330" width="40" height="92" rx="14" fill="${piel}"/>`,
        `<rect x="160" y="330" width="40" height="92" rx="14" fill="${piel}"/>`,
        `<rect x="86" y="308" width="128" height="26" rx="9" fill="${sh}"/>`,
        `<path d="M88 328 H146 V366 q0 8 -8 8 H96 q-8 0 -8 -8 Z" fill="${sh}"/>`,
        `<path d="M154 328 H212 V366 q0 8 -8 8 H162 q-8 0 -8 -8 Z" fill="${sh}"/>`,
        `<rect x="98" y="408" width="44" height="30" rx="8" fill="${me}"/>`,
        `<rect x="158" y="408" width="44" height="30" rx="8" fill="${me}"/>`,
        bootsInner(bo, a.botin_estilo),
    ].join('');
}

// Accesorios que van sobre la cabeza (después de dibujarla).
function headAccessoryInner(a) {
    if (a.accesorio === 'vincha') return `<rect x="104" y="60" width="92" height="13" rx="4" fill="${a.accesorio_color}"/>`;
    return '';
}

export function buildPlayerSvgBody(avatarInput, scale) {
    const a = normAvatar(avatarInput);
    const uid = Math.random().toString(36).slice(2, 8);
    const inner = `${upperBody(a, uid)}${legsInner(a)}${headInner(a, uid)}${headAccessoryInner(a)}`;
    if (scale && (scale.sx !== 1 || scale.sy !== 1)) {
        return `<g transform="translate(150 458) scale(${scale.sx.toFixed(3)} ${scale.sy.toFixed(3)}) translate(-150 -458)">${inner}</g>`;
    }
    return inner;
}

const VIEWBOX = '0 0 300 480';
const RATIO = 480 / 300;

export function avatarToDataUri(avatar, width = 200, altura, peso) {
    const body = buildPlayerSvgBody(avatar, avatarScale(altura, peso));
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${VIEWBOX}" width="${width}" height="${Math.round(width * RATIO)}">${body}</svg>`;
    return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}

export function PlayerAvatar({ avatar, size = 90, altura, peso }) {
    const html = useMemo(() => buildPlayerSvgBody(avatar, avatarScale(altura, peso)), [avatar, altura, peso]);
    return (
        <svg viewBox={VIEWBOX} width={size} height={Math.round(size * RATIO)} className="cs-avatar-svg" aria-hidden="true"
            dangerouslySetInnerHTML={{ __html: html }} />
    );
}
